// src/Dashboard/pages/teacher/GradeCN/components/GradeCNMobileCards.jsx

import React from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

import {
  DEFAULT_BELT_PHOTO,
  DEFAULT_USER_PHOTO,
  formatDateFr,
  getAdherentName,
  getAdherentPhoto,
  getBeltLabel,
  getBeltPhotoById,
  getNextBeltNameById,
  getNextBeltPhotoById,
  getUvCompletion,
} from "../utils/gradeCNDashboardUtils";

function UvBadge({ label, active }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-bold ${
        active
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700"
      }`}
    >
      {label} {active ? "✓" : "—"}
    </span>
  );
}

export default function GradeCNMobileCards({
  grades = [],
  belts = [],
  beltHistories = {},
  deletingId,
  onEdit,
  onDelete,
}) {
  return (
    <div className="space-y-3 md:hidden">
      {grades.map((grade) => {
        const adherent = grade?.Adherent || {};
        const history = beltHistories?.[grade.adherent_id];
        const completion = getUvCompletion(grade);

        const currentBeltPhoto = history?.belt_id
          ? getBeltPhotoById(belts, history.belt_id)
          : DEFAULT_BELT_PHOTO;

        const currentBelt = history?.belt_id
          ? belts.find((belt) => Number(belt.id) === Number(history.belt_id))
          : null;

        const currentBeltName = currentBelt ? getBeltLabel(currentBelt) : "—";

        const nextBeltPhoto = history?.belt_id
          ? getNextBeltPhotoById(belts, history.belt_id)
          : DEFAULT_BELT_PHOTO;

        const nextBeltName = history?.belt_id
          ? getNextBeltNameById(belts, history.belt_id)
          : "—";

        return (
          <article
            key={`mobile-grade-${grade.id}`}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60"
          >
            <div className="bg-slate-50 p-4 dark:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <img
                  src={getAdherentPhoto(adherent)}
                  alt={getAdherentName(adherent)}
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_USER_PHOTO;
                  }}
                  className="h-14 w-14 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />

                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-base font-bold text-slate-900 dark:text-white">
                    {getAdherentName(adherent) || "—"}
                  </h3>

                  <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                    Saison {adherent?.saison_active || "—"} · Dernier passage :{" "}
                    {history ? formatDateFr(history.date_passage) : "—"}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-4">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/50">
                <div>
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Actuelle
                  </p>

                  <img
                    src={currentBeltPhoto}
                    alt={currentBeltName}
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_BELT_PHOTO;
                    }}
                    className="h-8 object-contain"
                  />

                  <p className="mt-1 text-xs font-bold text-slate-600 dark:text-slate-300">
                    {currentBeltName}
                  </p>
                </div>

                <span className="text-xl font-bold text-slate-400">›</span>

                <div>
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Objectif
                  </p>

                  <img
                    src={nextBeltPhoto}
                    alt={nextBeltName}
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_BELT_PHOTO;
                    }}
                    className="h-8 object-contain"
                  />

                  <p className="mt-1 text-xs font-bold text-slate-600 dark:text-slate-300">
                    {nextBeltName}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                    grade.dominante === "Technique"
                      ? "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                      : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                  }`}
                >
                  {grade.dominante || "—"}
                </span>

                <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  UV3 : {Number(grade.uv3_points || 0)} pts
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <UvBadge label="UV1" active={completion.uv1} />
                <UvBadge label="UV2" active={completion.uv2} />
                <UvBadge label="UV3" active={completion.uv3} />
                <UvBadge label="UV4" active={completion.uv4} />
              </div>

              <div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]"
                    style={{ width: `${completion.percent}%` }}
                  />
                </div>

                <p className="mt-1 text-xs font-bold text-slate-500 dark:text-slate-400">
                  {completion.done}/{completion.total} UV · {completion.percent}%
                </p>
              </div>

              {grade.observations ? (
                <div className="rounded-2xl bg-slate-50 p-3 text-sm leading-6 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
                  {grade.observations}
                </div>
              ) : null}

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => onEdit(grade)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <FaEdit />
                  Modifier
                </button>

                <button
                  type="button"
                  onClick={() => onDelete(grade)}
                  disabled={deletingId === grade.id}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                >
                  <FaTrash />
                  Supprimer
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}